/**
 * Client Extracted Entities Tab
 *
 * Shows structured data that was pulled from the client's indexed documents
 * at upload time (ABN, ACN, directors, trustees, addresses, bank info, etc.)
 * Renders groups of entities with the documents they came from.
 */
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Database, FileText, Loader2, Sparkles } from 'lucide-react';
import apiClient from '@/services/api/client';
import { cn } from '@/lib/utils';

interface EntityDocument {
  file_id: string;
  filename: string;
  category: string;
  extracted_at: string;
  snippet: string | null;
}

interface EntityValue {
  value: string;
  entity_label: string | null;
  confidence: number;
  occurrences: number;
  documents: EntityDocument[];
}

interface EntityGroup {
  entity_type: string;
  label: string;
  values: EntityValue[];
}

interface ExtractedEntitiesResponse {
  clientId: string;
  summary: {
    total_entities: number;
    distinct_types: number;
    distinct_documents: number;
  };
  groups: EntityGroup[];
}

export interface ClientExtractedEntitiesTabProps {
  clientId: string;
}

function categoryLabel(c: string): string {
  return c.replace(/_/g, ' ').replace(/\b\w/g, s => s.toUpperCase());
}

function confidenceBadge(conf: number): string {
  if (conf >= 0.9) return 'bg-green-500/10 text-green-700 border-green-500/30';
  if (conf >= 0.7) return 'bg-blue-500/10 text-blue-700 border-blue-500/30';
  return 'bg-amber-500/10 text-amber-700 border-amber-500/30';
}

export function ClientExtractedEntitiesTab({ clientId }: ClientExtractedEntitiesTabProps) {
  const { data, isLoading, isError } = useQuery<ExtractedEntitiesResponse>({
    queryKey: ['client-entities', clientId],
    queryFn: async () => {
      const resp = await apiClient.get(`/clients/${clientId}/extracted-entities`);
      return resp.data?.data || resp.data;
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading extracted information…
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-destructive">
          Failed to load extracted entities.
        </CardContent>
      </Card>
    );
  }

  const { summary, groups } = data;

  if (summary.total_entities === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-10 flex flex-col items-center gap-2 text-center">
          <Database className="h-8 w-8 text-muted-foreground" />
          <p className="font-medium">No extracted information yet</p>
          <p className="text-sm text-muted-foreground max-w-md">
            Once this client's documents are indexed, structured fields like ABN, ACN, directors,
            trustees, addresses and bank details will appear here automatically.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#5048e5]" /> Extracted Information
          </CardTitle>
          <CardDescription>
            Structured data pulled from this client's indexed documents.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3 text-xs text-muted-foreground">
          <Badge variant="secondary">{summary.total_entities} facts</Badge>
          <Badge variant="secondary">{summary.distinct_types} types</Badge>
          <Badge variant="secondary">{summary.distinct_documents} documents</Badge>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {groups.map(group => (
          <Card key={group.entity_type}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{group.label}</CardTitle>
              <CardDescription className="text-[11px]">{group.values.length} distinct</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {group.values.map((v, i) => (
                <div key={i} className="rounded-md border p-2 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium break-words flex-1">{v.value}</p>
                    <Badge variant="outline" className={cn('text-[10px] border shrink-0', confidenceBadge(v.confidence))}>
                      {Math.round(v.confidence * 100)}%
                    </Badge>
                  </div>
                  {v.entity_label && v.entity_label !== v.value && (
                    <p className="text-[11px] text-muted-foreground italic">{v.entity_label}</p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {v.documents.slice(0, 3).map(doc => (
                      <Badge key={doc.file_id} variant="outline" className="text-[10px] font-normal">
                        <FileText className="h-3 w-3 mr-1" />
                        {doc.filename.length > 28 ? doc.filename.slice(0, 25) + '…' : doc.filename}
                        <span className="ml-1 text-muted-foreground">({categoryLabel(doc.category)})</span>
                      </Badge>
                    ))}
                    {v.documents.length > 3 && (
                      <Badge variant="outline" className="text-[10px] font-normal">
                        +{v.documents.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default ClientExtractedEntitiesTab;
